<script lang="ts">
  import { get } from "svelte/store";
  import { debounce } from "$src/application/main/debounce";
  import Input from "$lib/components/ui/input/input.svelte";
  import { createEventDispatcher, onDestroy, onMount, tick } from "svelte";
  import { cn } from "$lib/utils";
  import { useFolders } from "../hooks/useFolders";
  import { type Folder } from "$src/domain/customers/Folder";
  import { t } from "svelte-i18n";

  let container: HTMLElement;
  let listContainer: HTMLElement;

  export let patchHandler: () => void = () => {};
  export let inputHandler: () => void = () => {};

  export let value: string = "";

  export let groupStoreId: number;
  export let customerId: number;

  let isOpen = false;

  let searchValue: string = "";
  let selectedIndex = -1;

  $: folderQuery = useFolders(groupStoreId, customerId);

  const dispatch = createEventDispatcher();

  function selectFolder(folder: Folder) {
    value = folder.name;
    dispatch("selectFolder", folder);
    selectedIndex = -1;
    searchValue = value;
    isOpen = false;

    patchHandler();
    inputHandler();
  }

  const debouncedFetch = debounce((input: string) => {
    searchValue = value;
  }, 500);

  function handleInput() {
    isOpen = true;
    debouncedFetch(searchValue);

    patchHandler();
    inputHandler();
  }

  function showOptions(): void {
    searchValue = value;
    isOpen = true;
  }

  function hideOptions(): void {
    isOpen = false;
  }

  function handleClickOutside(event: MouseEvent) {
    if (container && !container.contains(event.target as Node)) {
      isOpen = false;
    }
  }

  async function handleKeydown(event: KeyboardEvent) {
    if (!showOptions) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      var query = get(folderQuery);
      if (query?.data) {
        selectedIndex = (selectedIndex + 1) % query.data.length;
        await tick();
        scrollToSelectedOption();
      }
    } else if (event.key === "ArrowUp") {
      event.preventDefault();

      var query = get(folderQuery);
      if (query?.data) {
        selectedIndex =
          (selectedIndex - 1 + query.data.length) % query.data.length;
        await tick();
        scrollToSelectedOption();
      }
    } else if (event.key === "Enter" && selectedIndex >= 0) {
      event.preventDefault();
      var query = get(folderQuery);
      if (query?.data) {
        selectFolder(query.data[selectedIndex]);
      }
    }
  }

  function scrollToSelectedOption() {
    const selectedElement = listContainer?.children[
      selectedIndex
    ] as HTMLElement;
    if (selectedElement) {
      const elementTop = selectedElement.offsetTop;
      const elementBottom = elementTop + selectedElement.offsetHeight;
      const containerScrollTop = listContainer.scrollTop;
      const containerHeight = listContainer.clientHeight;

      if (elementTop < containerScrollTop) {
        // Scroll up
        listContainer.scrollTop = elementTop;
      } else if (elementBottom > containerScrollTop + containerHeight) {
        // Scroll down
        listContainer.scrollTop = elementBottom - containerHeight;
      }
    }
  }

  onMount(() => {
    document.addEventListener("click", handleClickOutside);
    document.addEventListener("keydown", handleKeydown);
  });

  onDestroy(() => {
    document.removeEventListener("click", handleClickOutside);
    document.removeEventListener("keydown", handleKeydown);
  });
</script>

<div class="relative text-xs" bind:this={container}>
  <Input
    maxlength={50}
    bind:value
    on:input={handleInput}
    on:input={inputHandler}
    on:click={showOptions}
    on:focusin={showOptions}
  />
  {#if $folderQuery.isSuccess && isOpen}
    <ul
      class="absolute w-full mt-1 border bg-background rounded-lg shadow-lg max-h-44 overflow-auto z-10"
      bind:this={listContainer}
    >
      {#each $folderQuery.data as folder, i}
        <!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
        <!-- svelte-ignore a11y-click-events-have-key-events -->
        <li
          on:click={() => selectFolder(folder)}
          class={cn(
            "px-4 py-2 flex flex-row justify-between items-center cursor-pointer hover:bg-secondary hover:text-secondary-foreground",
            i == selectedIndex ? "bg-secondary text-secondary-foreground" : ""
          )}
        >
          <span class="font-semibold">
            {folder.name}
          </span>
        </li>
      {/each}
    </ul>
  {/if}
</div>
